import {GenericTable} from "@dvrt/graphql-widgets";
import {useDomainIDParam} from "@/types";
import {HasPermission, Link} from "../widgets";
import {useConcertoGenericQuery} from "@/hooks";
import {FaxListFragmentDoc, FaxListFragment} from "@/components/fax/queries.generated";
import CreateButton from "@/components/fax/create/CreateButton";
import {Box, Stack} from "@mui/material";
import SynchronizeButton from "./SynchronizeButton";
import {useDomainNameQuery} from "@/queries.generated";
import {ContextFilters} from "@/context/AuthProvider";
import {defineMessage, defineMessages} from "react-intl";
import {commonTranslations} from "@/utils/translations";
import TableShortcutButton from "../TableShortcutButton";
import {faTrash} from "@fortawesome/free-solid-svg-icons";

const FaxList = () => {
    const domainId = useDomainIDParam();

    const domainNameQuery = useDomainNameQuery({
        variables: {
            id: domainId,
        },
    });
    const domainName = domainNameQuery.data?.domains.items[0].name ?? "";

    const domainContextFilter = ContextFilters.domain(domainName);

    const query = useConcertoGenericQuery<FaxListFragment>("faxes", FaxListFragmentDoc, {
        filtering: {
            argumentName: "filter",
            placeHolder: commonTranslations.filterPlaceholders.e164OrShortNumber,
            idPrefix: "search-faxes",
        },
        fetchPolicy: "cache-and-network",
        extraVariables: {domainId},
        initialOrdering: ["number.shortNumber"],
    });

    return (
        <Box sx={{paddingX: 3}}>
            <GenericTable<FaxListFragment>
                query={query}
                labels={defineMessages({
                    incomingEmailAddress: {id: "incomingEmailAddress", defaultMessage: "Receiving email address"},
                    "number.shortNumber": {id: "shortNumber", defaultMessage: "Short number"},
                    "number.e164": {id: "e164", defaultMessage: "E.164"},
                    number: {id: "unused", defaultMessage: "Unused"},
                })}
                extraColumns={[
                    {
                        path: "actions",
                        label: null,
                        renderer: (row) => {
                            return (
                                <HasPermission name="delete_fax" contextFilter={domainContextFilter}>
                                    <TableShortcutButton
                                        shortcutPath={`${row.id}/delete`}
                                        tooltipTitle={commonTranslations.delete}
                                        buttonIcon={faTrash}
                                        color="secondary"
                                    />
                                </HasPermission>
                            );
                        },
                    },
                ]}
                createButton={
                    <HasPermission name="add_fax" contextFilter={domainContextFilter}>
                        <Stack direction="row" spacing={2}>
                            <CreateButton
                                successItemPath={"faxes"}
                                domainId={domainId}
                                onSuccess={query.refetch}
                                title={defineMessage({id: "new_fax", defaultMessage: "New fax"})}
                            />
                            <SynchronizeButton
                                domainId={domainId}
                                title={defineMessage({id: "synchronize", defaultMessage: "Synchronize"})}
                            />
                        </Stack>
                    </HasPermission>
                }
                dataRenderers={{
                    number: {
                        shortNumber: (value, _, rootRow) => {
                            return <Link to={`/domains/${domainId}/faxes/${rootRow.id}/settings`}>{value}</Link>;
                        },
                    },
                }}
                orderableFields="all"
            />
        </Box>
    );
};

export default FaxList;
